import Loki from 'lokijs';

let sharedEmployeeDB = null;
let sharedCrewDB = null;
let sharedTemplateDB = null;

const normalizeSortValue = (value) => {
  if (value == null) return '';
  return String(value)
    .replace(/[\s\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000\uFEFF]+/g, ' ')
    .trim()
    .toLowerCase();
};

const alphanumericCompare = (aVal, bVal, isDescending) => {
  if (aVal == null && bVal == null) return 0;
  if (aVal == null) return isDescending ? -1 : 1;
  if (bVal == null) return isDescending ? 1 : -1;

  const a = normalizeSortValue(aVal);
  const b = normalizeSortValue(bVal);
  
  const reA = /[^\d]+|\d+/g;
  const chunksA = a.match(reA) || [];
  const chunksB = b.match(reA) || [];

  const maxLen = Math.max(chunksA.length, chunksB.length);

  for (let i = 0; i < maxLen; i++) {
    const chunkA = chunksA[i] || '';
    const chunkB = chunksB[i] || '';

    const numA = parseFloat(chunkA);
    const numB = parseFloat(chunkB);

    if (!isNaN(numA) && !isNaN(numB)) {
      if (numA !== numB) {
        const result = numA - numB;
        return isDescending ? -result : result;
      }
    } else {
      if (chunkA < chunkB) return isDescending ? 1 : -1;
      if (chunkA > chunkB) return isDescending ? -1 : 1;
    }
  }

  return 0;
};


export const getEmployeeDB = () => {
  if (!sharedEmployeeDB) {
    sharedEmployeeDB = new Loki('employees.db');
    sharedEmployeeDB.addCollection('employees', {
      unique: ['_id']
    });
  }
  return sharedEmployeeDB;
};


export const getCrewDB = () => {
  if (!sharedCrewDB) {
    sharedCrewDB = new Loki('crews.db');
    sharedCrewDB.addCollection('crews', {
      unique: ['_id']
    });
  }
  return sharedCrewDB;
};

export const getTemplateDB = () => {
  if (!sharedTemplateDB) {
    sharedTemplateDB = new Loki('templates.db');
    sharedTemplateDB.addCollection('templates', {
      unique: ['_id']
    });
  }
  return sharedTemplateDB;
};

/**
 * Initialize the crew database with data
 * @param {Array} data - Crew data array
 */
export const initializeCrewDB = (data) => {
  const db = getCrewDB();
  const collection = db.getCollection('crews');
  
  if (collection) {
    collection.clear();
    data.forEach(crew => {
      collection.insert(JSON.parse(JSON.stringify(crew)));
    });
  }
  
  return db;
};

/**
 * Sort employees using LokiJS while maintaining scheduled/unscheduled separation
 * @param {Array} data - Employee data array
 */
export const initializeEmployeeDB = (data) => {
  const db = getEmployeeDB();
  const collection = db.getCollection('employees');
  
  if (collection) {
    collection.clear();
    data.forEach(emp => {
      collection.insert(JSON.parse(JSON.stringify(emp)));
    });
  }
  
  return db;
};

/**
 * Sort employees using LokiJS while maintaining scheduled/unscheduled separation
 * @param {Array} originalData - Original unsorted employee data
 * @param {string} field - Field to sort by
 * @param {string} order - Sort order ('ascend' or 'descend')
 * @param {Function} setLoading - Loading state setter (optional)
 * @returns {Promise<Array>} Sorted employee data
 */
export const sortEmployees = (originalData, field, order, setLoading = null) => {
  return new Promise((resolve) => {
    // No sorting - return original data
    if (!order) {
      resolve([...originalData]);
      return;
    }

    const db = getEmployeeDB();
    const collection = db.getCollection('employees');
    
    if (!collection) {
      resolve([...originalData]);
      return;
    }

    // Show loader for large datasets (>10k records)
    const isLargeDataset = originalData.length > 10000;
    if (isLargeDataset && setLoading) {
      setLoading(true);
    }

    setTimeout(() => {
      try {
        const withCrew = originalData.filter(emp => Boolean(emp.currentCrew));
        const withoutCrew = originalData.filter(emp => !emp.currentCrew);

        const isDescending = order === 'descend';

        const compareFunction = (a, b) => {
          return alphanumericCompare(a?.[field], b?.[field], isDescending);
        };

        const sortedWithCrew = withCrew.length > 0 
          ? [...withCrew].sort(compareFunction)
          : [];

        const sortedWithoutCrew = withoutCrew.length > 0
          ? [...withoutCrew].sort(compareFunction)
          : [];

        const cleanedData = [...sortedWithCrew, ...sortedWithoutCrew];
        
      

        resolve(cleanedData);
      } catch (error) {
        console.error('Error during sort:', error);
        resolve([...originalData]);
      } finally {
        if (isLargeDataset && setLoading) {
          setLoading(false);
        }
      }
    }, 0);
  });
};

/**
 * Sort crews using LokiJS
 * @param {Array} originalData - Original unsorted crew data
 * @param {string} field - Field to sort by
 * @param {string} order - Sort order ('ascend' or 'descend')
 * @param {Function} setLoading - Loading state setter (optional)
 * @returns {Promise<Array>} Sorted crew data
 */
export const sortCrews = (originalData, field, order, setLoading = null) => {
  return new Promise((resolve) => {
    // No sorting - return original data
    if (!order) {
      resolve([...originalData]);
      return;
    }

    const db = getCrewDB();
    const collection = db.getCollection('crews');
    
    if (!collection) {
      resolve([...originalData]);
      return;
    }

    const isLargeDataset = originalData.length > 1000;
    if (isLargeDataset && setLoading) {
      setLoading(true);
    }

    setTimeout(() => {
      try {
        const isDescending = order === 'descend';

        const compareFunction = (a, b) => {
          return alphanumericCompare(a?.[field], b?.[field], isDescending);
        };

        const cleanedData = [...originalData].sort(compareFunction);
        
       

        resolve(cleanedData);
      } catch (error) {
        console.error('Error during crew sort:', error);
        resolve([...originalData]);
      } finally {
        if (isLargeDataset && setLoading) {
          setLoading(false);
        }
      }
    }, 0);
  });
};

/**
 * Sort crew templates using LokiJS
 * @param {Array} originalData - Original unsorted template data
 * @param {string} field - Field to sort by
 * @param {string} order - Sort order ('ascend' or 'descend')
 * @param {Function} setLoading - Loading state setter (optional)
 * @returns {Promise<Array>} Sorted template data
 */
export const sortTemplates = (originalData, field, order, setLoading = null) => {
  return new Promise((resolve) => {
    if (!order) {
      resolve([...originalData]);
      return;
    }

    const db = getTemplateDB();
    const collection = db.getCollection('templates');
    
    if (!collection) {
      resolve([...originalData]);
      return;
    }

    const isLargeDataset = originalData.length > 1000;
    if (isLargeDataset && setLoading) {
      setLoading(true);
    }

    setTimeout(() => {
      try {
        const isDescending = order === 'descend';

        const compareFunction = (a, b) => {
          return alphanumericCompare(a?.[field], b?.[field], isDescending);
        };

        const cleanedData = [...originalData].sort(compareFunction);
        
       

        resolve(cleanedData);
      } catch (error) {
        console.error('Error during template sort:', error);
        resolve([...originalData]);
      } finally {
        if (isLargeDataset && setLoading) {
          setLoading(false);
        }
      }
    }, 0);
  });
};
